import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNovaPoshtaDelivery } from '../hooks/useNovaPoshtaDelivery';

const METHOD_WAREHOUSE = 'warehouse';
const METHOD_COURIER = 'courier';

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    marginTop: 5,
    borderRadius: 4,
    border: '1px solid #ccc',
    boxSizing: 'border-box',
    fontSize: 16,
};

const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid #ccc',
    borderTop: 'none',
    borderRadius: '0 0 4px 4px',
    maxHeight: 220,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    zIndex: 100,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};

const dropdownItemStyle = (highlighted) => ({
    padding: '12px 14px',
    cursor: 'pointer',
    background: highlighted ? '#f0f4ff' : '#fff',
    fontSize: 15,
    borderBottom: '1px solid #f0f0f0',
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
});

const labelStyle = { display: 'block', fontSize: 13, color: '#555', marginBottom: 3, marginTop: 12 };
const errorStyle = { color: 'red', fontSize: 12, marginTop: 3 };
const spinnerStyle = {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid #ccc',
    borderTopColor: '#555',
    borderRadius: '50%',
    animation: 'np-spin 0.6s linear infinite',
    verticalAlign: 'middle',
    marginLeft: 6,
};

function Autocomplete({ label, placeholder, value, onSelect, onSearch, required, disabled, minLength = 2 }) {
    const [inputValue, setInputValue] = useState(value?.label || '');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const [touched, setTouched] = useState(false);
    const containerRef = useRef(null);
    const pendingRef = useRef(null);
    const userTypingRef = useRef(false);

    useEffect(() => {
        if (userTypingRef.current) {
            userTypingRef.current = false;
            return;
        }
        setInputValue(value?.label || '');
        if (!value) {
            setItems([]);
            setOpen(false);
        }
    }, [value]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const handleInput = useCallback(async (e) => {
        const q = e.target.value;
        userTypingRef.current = true;
        setInputValue(q);
        onSelect(null);
        setError('');
        setHighlighted(-1);

        if (!q || q.trim().length < minLength) {
            setItems([]);
            setOpen(false);
            return;
        }

        setLoading(true);
        setOpen(true);
        const id = {};
        pendingRef.current = id;
        try {
            const result = await onSearch(q);
            if (pendingRef.current !== id) return;
            setItems(result || []);
        } catch {
            if (pendingRef.current !== id) return;
            setError('Помилка завантаження. Спробуйте ще раз.');
            setItems([]);
        } finally {
            if (pendingRef.current === id) setLoading(false);
        }
    }, [onSearch, onSelect]);

    const handleSelect = useCallback((item) => {
        setInputValue(item.label);
        setItems([]);
        setOpen(false);
        setHighlighted(-1);
        onSelect(item);
    }, [onSelect]);

    const handleKeyDown = (e) => {
        if (!open || items.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted(h => Math.min(h + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlighted >= 0) handleSelect(items[highlighted]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div style={{ marginBottom: 8 }}>
            <style>{`@keyframes np-spin { to { transform: rotate(360deg); } }`}</style>
            <label style={labelStyle}>
                {label}{required && <span style={{ color: 'red' }}> *</span>}
                {loading && <span style={spinnerStyle} />}
            </label>
            <div ref={containerRef} style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleInput}
                    onKeyDown={handleKeyDown}
                    onFocus={() => items.length > 0 && setOpen(true)}
                    onBlur={() => setTouched(true)}
                    disabled={disabled}
                    autoComplete="off"
                    style={{
                        ...inputStyle,
                        background: disabled ? '#f5f5f5' : '#fff',
                        cursor: disabled ? 'not-allowed' : 'text',
                        border: touched && required && !value && inputValue
                            ? '1px solid red'
                            : '1px solid #ccc',
                    }}
                />
                {open && items.length > 0 && (
                    <div style={dropdownStyle}>
                        {items.map((item, idx) => (
                            <div
                                key={item.value}
                                style={dropdownItemStyle(idx === highlighted)}
                                onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
                                onMouseEnter={() => setHighlighted(idx)}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                )}
                {open && !loading && items.length === 0 && inputValue.trim().length >= minLength && (
                    <div style={{ ...dropdownStyle, padding: '10px 12px', color: '#888', fontSize: 13 }}>
                        Нічого не знайдено
                    </div>
                )}
            </div>
            {error && <div style={errorStyle}>{error}</div>}
            {touched && required && !value && inputValue && !error && (
                <div style={errorStyle}>Оберіть значення зі списку</div>
            )}
        </div>
    );
}

function cityToOption(c) {
    return {
        value: c.Ref,
        label: `${c.Description}${c.AreaDescription ? `, ${c.AreaDescription} обл.` : ''}`,
        ref: c.Ref,
    };
}

function warehouseToOption(w) {
    return {
        value: w.Ref,
        label: w.Description,
        ref: w.Ref,
        shortAddress: w.ShortAddress,
    };
}

function streetToOption(s) {
    return {
        value: s.Ref,
        label: [s.StreetsTypeDescription, s.Description].filter(Boolean).join(' '),
        ref: s.Ref,
    };
}

function DeliverySelect({ value, onChange, submitted }) {
    const [method, setMethod] = useState(METHOD_WAREHOUSE);
    const [city, setCity] = useState(null);
    const [warehouse, setWarehouse] = useState(null);
    const [street, setStreet] = useState(null);
    const [house, setHouse] = useState('');
    const { searchCities, searchWarehouses, searchStreets } = useNovaPoshtaDelivery();

    const handleCitySearch = useCallback(
        async (q) => (await searchCities(q)).map(cityToOption),
        [searchCities]
    );

    const handleWarehouseSearch = useCallback(
        async (q) => {
            if (!city) return [];
            const all = await searchWarehouses(city.ref);
            const filtered = (all || []).filter(w =>
                w.Description.toLowerCase().includes(q.toLowerCase())
            );
            return filtered.map(warehouseToOption);
        },
        [searchWarehouses, city]
    );

    const handleStreetSearch = useCallback(
        async (q) => {
            if (!city) return [];
            return (await searchStreets(city.ref, q)).map(streetToOption);
        },
        [searchStreets, city]
    );

    const handleMethodChange = (m) => {
        setMethod(m);
        setCity(null);
        setWarehouse(null);
        setStreet(null);
        setHouse('');
        onChange(null);
    };

    const handleCitySelect = (item) => {
        setCity(item);
        setWarehouse(null);
        setStreet(null);
        setHouse('');
        onChange(null);
    };

    useEffect(() => {
        if (method === METHOD_WAREHOUSE) {
            if (city && warehouse) {
                onChange({
                    method: METHOD_WAREHOUSE,
                    cityRef: city.ref,
                    cityName: city.label,
                    warehouseRef: warehouse.ref,
                    warehouseName: warehouse.label,
                });
            } else {
                onChange(null);
            }
        } else {
            if (city && street && house.trim()) {
                onChange({
                    method: METHOD_COURIER,
                    cityRef: city.ref,
                    cityName: city.label,
                    streetRef: street.ref,
                    streetName: street.label,
                    house: house.trim(),
                });
            } else {
                onChange(null);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [method, city, warehouse, street, house]);

    const isDeliveryMissing = submitted && !value;

    return (
        <div style={{ marginBottom: 15, marginTop: 5 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 10 }}>
                Спосіб доставки<span style={{ color: 'red' }}> *</span>
            </label>

            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                    { id: METHOD_WAREHOUSE, label: 'Відділення / поштомат' },
                    { id: METHOD_COURIER, label: 'Кур\'єр' },
                ].map(opt => (
                    <label
                        key={opt.id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: 'pointer',
                            padding: '10px 14px',
                            border: `2px solid ${method === opt.id ? '#1a73e8' : '#ccc'}`,
                            borderRadius: 6,
                            background: method === opt.id ? '#eef4ff' : '#fff',
                            fontSize: 15,
                            fontWeight: method === opt.id ? 600 : 400,
                            transition: 'all 0.15s',
                            flex: '1 1 140px',
                            minHeight: 44,
                            userSelect: 'none',
                        }}
                    >
                        <input
                            type="radio"
                            name="deliveryMethod"
                            value={opt.id}
                            checked={method === opt.id}
                            onChange={() => handleMethodChange(opt.id)}
                            style={{ accentColor: '#1a73e8' }}
                        />
                        {opt.label}
                    </label>
                ))}
            </div>

            <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: '12px 14px' }}>
                <Autocomplete
                    label="Місто"
                    placeholder="Введіть назву міста..."
                    value={city}
                    onSelect={handleCitySelect}
                    onSearch={handleCitySearch}
                    required
                />

                {method === METHOD_WAREHOUSE && (
                    <Autocomplete
                        label="Відділення або поштомат"
                        placeholder={city ? 'Пошук за номером або адресою...' : 'Спочатку оберіть місто'}
                        value={warehouse}
                        onSelect={setWarehouse}
                        onSearch={handleWarehouseSearch}
                        disabled={!city}
                        required
                        minLength={1}
                    />
                )}

                {method === METHOD_COURIER && (
                    <>
                        <Autocomplete
                            label="Вулиця"
                            placeholder={city ? 'Введіть назву вулиці...' : 'Спочатку оберіть місто'}
                            value={street}
                            onSelect={setStreet}
                            onSearch={handleStreetSearch}
                            disabled={!city}
                            required
                        />
                        <div style={{ marginBottom: 8 }}>
                            <label style={labelStyle}>
                                Будинок<span style={{ color: 'red' }}> *</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Наприклад: 15А"
                                value={house}
                                onChange={(e) => setHouse(e.target.value)}
                                disabled={!street}
                                style={{
                                    ...inputStyle,
                                    width: '100%',
                                    maxWidth: 160,
                                    background: !street ? '#f5f5f5' : '#fff',
                                    cursor: !street ? 'not-allowed' : 'text',
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            {isDeliveryMissing && (
                <div style={{ color: 'red', fontSize: 13, marginTop: 6 }}>
                    Будь ласка, вкажіть адресу доставки
                </div>
            )}
        </div>
    );
}

export default DeliverySelect;
